import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { approvalsChanged, approverText, DOC_ICON, DOC_LABEL, docLink, type Approval, type ApprovalState } from '../lib/approvals.ts';
import { dateTime, money } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction, useSave } from './manage/common.tsx';

type Box = 'decide' | 'mine' | 'all';
const BOXES: { key: Box; icon: IconName; label: MessageKey }[] = [
  { key: 'decide', icon: 'shieldCheck', label: 'appr.box_decide' },
  { key: 'mine', icon: 'send', label: 'appr.box_mine' },
  { key: 'all', icon: 'list', label: 'appr.box_all' },
];
const STATE: Record<ApprovalState, { tone: Tone; icon: IconName; label: MessageKey }> = {
  pending: { tone: 'warn', icon: 'clock', label: 'appr.st_pending' },
  approved: { tone: 'ok', icon: 'checkCircle', label: 'appr.st_approved' },
  rejected: { tone: 'danger', icon: 'x', label: 'appr.st_rejected' },
  withdrawn: { tone: 'neutral', icon: 'undo', label: 'appr.st_withdrawn' },
};
export const stateBadge = (s: ApprovalState) => <StatusBadge tone={STATE[s].tone} icon={STATE[s].icon} label={t(STATE[s].label)} />;

/**
 * Approvals: what waits for this person's yes, what they asked for themselves and, for managers,
 * every request. Answering takes one tap; a "no" says why, so the person who asked can fix it.
 */
export function Approvals() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const boxes = BOXES.filter((b) => b.key !== 'all' || can('studio.manage'));
  const box = (boxes.some((b) => b.key === params.get('box')) ? params.get('box') : 'decide') as Box;
  const { data, reload } = useAction<Approval[]>('studio.approvals', { box });
  const [open, setOpen] = useState<Approval | null>(null);

  return (
    <Shell>
      <div className="page approvals">
        <div className="page-head">
          <div>
            <h1>{t('appr.title')}</h1>
            <p className="muted">{t('appr.subtitle')}</p>
          </div>
        </div>
        <div className="segmented tabs" role="tablist" aria-label={t('appr.title')}>
          {boxes.map((b) => (
            <button key={b.key} role="tab" aria-selected={box === b.key} className={box === b.key ? 'active' : ''} onClick={() => setParams(b.key === 'decide' ? {} : { box: b.key }, { replace: true })}>
              <Icon name={b.icon} size={16} />
              {t(b.label)}
            </button>
          ))}
        </div>
        {!data ? (
          <Skeleton h={240} r="var(--radius-lg)" />
        ) : !data.length ? (
          <EmptyState
            icon={box === 'decide' ? 'checkCircle' : 'send'}
            title={box === 'decide' ? t('appr.empty_decide') : box === 'mine' ? t('appr.empty_mine') : t('appr.empty_all')}
            body={box === 'decide' ? t('appr.empty_decide_body') : undefined}
          />
        ) : (
          <div className="list-card" role="list">
            {data.map((a) => (
              <div key={a.id} role="listitem" className="list-row appr-row">
                <span className="list-icon" aria-hidden="true">
                  <Icon name={DOC_ICON[a.document]} size={18} />
                </span>
                <button className="list-main" onClick={() => setOpen(a)}>
                  <span className="list-title">{a.label}</span>
                  <span className="list-sub">
                    {[t(DOC_LABEL[a.document]), box === 'mine' ? approverText(a) : a.requested_by_name, dateTime(a.requested_at)].filter(Boolean).join(' · ')}
                  </span>
                  {a.note ? <span className="appr-note">“{a.note}”</span> : null}
                </button>
                <span className="appr-end">
                  <span className="tabular appr-amount">{money(a.amount)}</span>
                  {stateBadge(a.state)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      {open ? (
        <ApprovalDialog
          a={open}
          canDecide={box === 'decide'}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            approvalsChanged();
            void reload();
          }}
        />
      ) : null}
    </Shell>
  );
}

function ApprovalDialog({ a, canDecide, onClose, onDone }: { a: Approval; canDecide: boolean; onClose: () => void; onDone: () => void }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const { busy, run } = useSave();
  const [saying, setSaying] = useState(false);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const decide = (approve: boolean) => {
    if (!approve) {
      setTried(true);
      if (!reason.trim()) return;
    }
    void run(async () => {
      await action('studio.decide_approval', { approval: a.id, approve, reason: approve ? null : reason.trim() });
      onDone();
    }, approve ? t('appr.approved_done') : t('appr.rejected_done'));
  };
  const withdraw = () =>
    void run(async () => {
      await action('studio.withdraw_approval', { approval: a.id });
      onDone();
    }, t('appr.withdrawn_done'));
  const pending = a.state === 'pending';
  const answer = pending && canDecide;

  return (
    <Dialog
      title={t(DOC_LABEL[a.document])}
      onClose={onClose}
      size="sm"
      footer={
        answer ? (
          saying ? (
            <>
              <Button variant="ghost" onClick={() => setSaying(false)}>
                {t('common.back')}
              </Button>
              <Button variant="danger" icon="x" onClick={() => decide(false)} busy={busy}>
                {t('appr.reject')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" icon="x" onClick={() => setSaying(true)} disabled={busy}>
                {t('appr.reject')}
              </Button>
              <Button variant="success" icon="check" onClick={() => decide(true)} busy={busy} data-autofocus>
                {t('appr.approve')}
              </Button>
            </>
          )
        ) : (
          <>
            {pending && a.requested_by === me?.userId ? (
              <Button variant="ghost" icon="undo" className="dialog-left" onClick={withdraw} busy={busy}>
                {t('appr.withdraw')}
              </Button>
            ) : null}
            <Button variant="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
          </>
        )
      }
    >
      <div className="appr-gate">
        <div className="appr-doc">
          <span className="list-icon" aria-hidden="true">
            <Icon name={DOC_ICON[a.document]} size={18} />
          </span>
          <span className="appr-doc-main">
            <span className="list-title">{a.label}</span>
            <button type="button" className="link-button" onClick={() => navigate(docLink(a.document, a.record))}>
              {t('appr.open_doc')}
            </button>
          </span>
          <span className="appr-amount tabular">{money(a.amount)}</span>
        </div>
        <dl className="appr-facts">
          <dt>{t('appr.asked_by')}</dt>
          <dd>
            {a.requested_by_name ?? '—'} · {dateTime(a.requested_at)}
          </dd>
          {a.note ? (
            <>
              <dt>{t('appr.note')}</dt>
              <dd>{a.note}</dd>
            </>
          ) : null}
          <dt>{t('appr.approver')}</dt>
          <dd>{approverText(a)}</dd>
          <dt>{t('appr.state')}</dt>
          <dd>{stateBadge(a.state)}</dd>
          {a.decided_at && a.state !== 'withdrawn' ? (
            <>
              <dt>{t('appr.answered_by')}</dt>
              <dd>
                {a.decided_by_name ?? '—'} · {dateTime(a.decided_at)}
              </dd>
            </>
          ) : null}
          {a.reason ? (
            <>
              <dt>{t('appr.reason')}</dt>
              <dd>{a.reason}</dd>
            </>
          ) : null}
        </dl>
        {answer && saying ? (
          <Field label={t('appr.reason')} hint={t('appr.reason_hint')} error={tried && !reason.trim() ? t('form.required') : null} required counter={`${reason.length}/300`}>
            <textarea className="input" rows={3} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-autofocus />
          </Field>
        ) : null}
      </div>
    </Dialog>
  );
}
